import * as React from 'react';
import { observer } from 'mobx-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Firmware, FirmwareProps, FirmwareType } from '@ixicare/resources';
import { Button, CreateButton, IconButton, Panel, Spinner, Table, formatDate } from '@ixicare/ui';
import { Trash2 } from 'lucide-react';
import { t } from '~i18n';
import { useStores } from '~store/utils';
import { humaniseErrors } from '~utils/error';
import { FirmwareUploadModal } from '../components/FirmwareUploadModal';
import { FIRMWARES_QUERY_KEY, useFirmwares } from '../hooks/useFirmwares';
import { firmwareDevicesLabel, firmwareHardwareLabel } from '../utils/firmwareLabels';
import styles from './FirmwareList.module.css';

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

const byVersionDesc = (a: FirmwareProps, b: FirmwareProps) =>
  b.version.localeCompare(a.version, undefined, { numeric: true });

const groupByType = (firmwares: FirmwareProps[]) => {
  const groups = new Map<FirmwareType, FirmwareProps[]>();
  firmwares.forEach((firmware) => {
    groups.set(firmware.firmwareType, [...(groups.get(firmware.firmwareType) ?? []), firmware]);
  });
  return [...groups.entries()]
    .map(([type, items]) => ({ type, items: items.sort(byVersionDesc) }))
    .sort((a, b) => a.type.localeCompare(b.type));
};

const FirmwareList = observer(() => {
  const { notifications, confirmation } = useStores();
  const queryClient = useQueryClient();
  const { data: firmwares, isLoading } = useFirmwares();
  const [uploadType, setUploadType] = React.useState<FirmwareType | null | undefined>(undefined);

  const groups = React.useMemo(() => groupByType(firmwares ?? []), [firmwares]);

  const notifyError = (error: unknown) =>
    notifications.addUserActionNotification({
      type: 'error' as any,
      notificationType: 'error',
      message: humaniseErrors(error as Error).title,
    });

  // Customers can only update to the latest, so a type always keeps one: marking another replaces it.
  const latestMutation = useMutation({
    mutationFn: (firmware: FirmwareProps) => Firmware.updateFirmware(firmware.id, { isLatest: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FIRMWARES_QUERY_KEY }),
    onError: notifyError,
  });

  const handleDelete = (firmware: FirmwareProps) => {
    confirmation.show({
      title: t('firmware.delete.confirmTitle'),
      message: t('firmware.delete.confirmMessage', {
        type: firmwareDevicesLabel(firmware.firmwareType),
        version: firmware.version,
      }),
      dangerous: true,
      onConfirm: () =>
        Firmware.deleteFirmware(firmware.id)
          .then(() => {
            queryClient.invalidateQueries({ queryKey: FIRMWARES_QUERY_KEY });
            notifications.addUserActionNotification({
              type: 'success' as any,
              notificationType: 'success',
              message: t('firmware.delete.success'),
            });
          })
          .catch(notifyError),
      onCancel: () => {},
    });
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <>
      {groups.length === 0 && (
        <Panel>
          <p className={styles.Empty}>{t('firmware.empty')}</p>
        </Panel>
      )}
      {groups.map(({ type, items }) => (
        <Panel key={type}>
          <Panel.Title
            extraInfo={
              <Button buttonType="link" onClick={() => setUploadType(type)}>
                {t('firmware.upload.newVersion')}
              </Button>
            }
          >
            <span className={styles.TypeTitle}>
              {firmwareDevicesLabel(type)}
              <span className={styles.TypeHardware}>{firmwareHardwareLabel(type)}</span>
            </span>
          </Panel.Title>
          <Table className={styles.CompactTable}>
            <Table.Head>
              <Table.Row>
                <Table.HeadCol>{t('firmware.version')}</Table.HeadCol>
                <Table.HeadCol>{t('firmware.uploaded')}</Table.HeadCol>
                <Table.HeadCol>{t('firmware.size')}</Table.HeadCol>
                <Table.HeadCol />
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {items.map((firmware) => (
                <Table.Row key={firmware.id}>
                  <Table.Col>
                    <span className={styles.Version}>
                      {firmware.version}
                      {firmware.isLatest && <span className={styles.LatestBadge}>{t('firmware.latest')}</span>}
                    </span>
                  </Table.Col>
                  <Table.Col>{formatDate(firmware.created)}</Table.Col>
                  <Table.Col>{formatSize(firmware.length)}</Table.Col>
                  <Table.Col>
                    <div className={styles.Actions}>
                      {!firmware.isLatest && (
                        <button
                          type="button"
                          className={styles.TextAction}
                          disabled={latestMutation.isPending}
                          onClick={() => latestMutation.mutate(firmware)}
                        >
                          {t('firmware.markLatest')}
                        </button>
                      )}

                      {firmware.isLatest && items.length > 1 ? (
                        <span className={styles.DisabledIcon} title={t('firmware.delete.latestBlocked')}>
                          <IconButton lucide={Trash2} />
                        </span>
                      ) : (
                        <span title={t('residents.table.delete')}>
                          <IconButton lucide={Trash2} onClick={() => handleDelete(firmware)} />
                        </span>
                      )}
                    </div>
                  </Table.Col>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Panel>
      ))}
      <CreateButton atBottom onClick={() => setUploadType(null)} />
      {uploadType !== undefined && (
        <FirmwareUploadModal defaultFirmwareType={uploadType ?? undefined} onClose={() => setUploadType(undefined)} />
      )}
    </>
  );
});

export default FirmwareList;
